/* Approved Selective homepage treatment. No navigation or commerce interception. */
(() => {
  const reduced = matchMedia('(prefers-reduced-motion: reduce)');
  const narrow = matchMedia('(max-width:767px)');
  const targets = '.hp-wed-text,.hp-meet-portrait,.hp-meet-text,.hp-visit-img';
  const running = new Set();
  const pending = new Set();
  const revealed = new WeakSet();
  const images = new WeakSet();
  let observer, resizeFrame, lifecycle = 0;

  function animate(el, frames, duration, delay = 0, easing = 'cubic-bezier(.22,1,.36,1)') {
    if (!el || reduced.matches || !el.animate) return;
    const animation = el.animate(frames, { duration, delay, easing, fill: 'backwards' });
    running.add(animation);
    animation.finished.then(() => running.delete(animation), () => running.delete(animation));
  }
  function clear(el) {
    el.removeAttribute('data-motion-pending');
    pending.delete(el);
    observer?.unobserve(el);
    revealed.add(el);
  }
  function cancel(scope = document) {
    for (const el of [...pending]) if (scope.contains(el)) clear(el);
    for (const animation of [...running]) if (scope.contains(animation.effect?.target)) {
      animation.cancel();
      running.delete(animation);
    }
  }
  function curtain(el, photograph = false) {
    const mask = { maskImage: 'linear-gradient(#000 50%,transparent 50%)', maskRepeat: 'no-repeat', maskSize: '100% 200%' };
    animate(el, [
      { ...mask, maskPosition: 'left 100%', transform: photograph ? 'scale(1.05)' : 'none' },
      { ...mask, maskPosition: 'left 0%', transform: 'none' }
    ], 900, 0, 'cubic-bezier(.19,1,.22,1)');
  }
  function rise(el, delay) {
    animate(el, [{ opacity: 0, translate: `0 ${narrow.matches ? 16 : 24}px` }, { opacity: 1, translate: '0 0' }], 600, delay, 'cubic-bezier(.16,1,.3,1)');
  }
  function entrance(el) {
    clear(el);
    if (reduced.matches) return;
    if (el.matches('.hp-meet-portrait')) curtain(el, true);
    else if (el.matches('.hp-visit-img')) animate(el, [{ opacity: .65 }, { opacity: 1 }], 900, 0, 'cubic-bezier(.25,.1,.25,1)');
    else {
      curtain(el.querySelector('h2'));
      if (el.matches('.hp-wed-text')) rise(el.querySelector('.hp-wed-btn'), 80);
      else {
        rise(el.querySelector('.hp-meet-body'), 80);
        rise(el.querySelector('.hp-meet-accent'), 160);
        rise(el.querySelector('.hp-meet-btn'), 240);
      }
    }
  }
  function resetObserver() {
    observer?.disconnect();
    if (reduced.matches || !window.IntersectionObserver) return;
    observer = new IntersectionObserver(entries => {
      for (const entry of entries) if (entry.isIntersecting && entry.intersectionRatio >= .1) entrance(entry.target);
    }, { threshold: .1, rootMargin: `0px 0px -${Math.round(innerHeight * .18)}px 0px` });
    for (const el of pending) observer.observe(el);
  }
  function prepare(scope = document) {
    if (reduced.matches || !observer || !Element.prototype.animate) return;
    for (const el of scope.querySelectorAll(targets)) {
      if (revealed.has(el) || pending.has(el)) continue;
      const rect = el.getBoundingClientRect();
      // Restored scroll positions and visible editor sections stay readable.
      if (rect.top < innerHeight) { revealed.add(el); continue; }
      el.setAttribute('data-motion-pending', el.matches('.hp-meet-portrait') ? 'wipe' : el.matches('img') ? 'photo' : 'sequence');
      pending.add(el);
      observer.observe(el);
    }
  }
  function prepareImages(scope = document) {
    for (const image of scope.querySelectorAll('.pcard-rail-alt')) {
      if (images.has(image)) continue;
      images.add(image);
      const ready = () => {
        if (image.naturalWidth) image.closest('.pcard-rail')?.classList.add('pcard-alt-ready');
      };
      if (image.complete) ready();
      else image.addEventListener('load', ready, { once: true });
    }
  }
  function hero() {
    const text = document.querySelector('.hp-hero-text');
    if (!text || scrollY > 0 || reduced.matches) return;
    animate(text, [{ opacity: .15, translate: narrow.matches ? '0 0' : '0 8px' }, { opacity: 1, translate: '0 0' }], narrow.matches ? 400 : 600);
    animate(document.querySelector('.hp-hero-img'), [{ opacity: .65, transform: narrow.matches ? 'none' : 'scale(1.015)' }, { opacity: 1, transform: 'none' }], narrow.matches ? 500 : 850);
  }

  resetObserver();
  prepare();
  prepareImages();
  const initial = lifecycle;
  // A stalled font or photograph must not defer an entrance indefinitely.
  Promise.race([
    Promise.all([document.fonts.ready, document.querySelector('.hp-hero-img')?.decode().catch(() => {})]),
    new Promise(resolve => setTimeout(resolve, 1500))
  ]).then(() => { if (lifecycle === initial) hero(); });

  reduced.addEventListener('change', () => {
    lifecycle++;
    cancel();
    resetObserver();
  });
  window.addEventListener('resize', () => {
    cancelAnimationFrame(resizeFrame);
    resizeFrame = requestAnimationFrame(resetObserver);
  });
  window.addEventListener('pagehide', () => {
    lifecycle++;
    cancel();
    observer?.disconnect();
    cancelAnimationFrame(resizeFrame);
  });
  window.addEventListener('pageshow', event => {
    if (event.persisted) { resetObserver(); prepareImages(); }
  });
  document.addEventListener('focusin', event => {
    const group = event.target.closest(targets);
    if (group) cancel(group);
    for (const animation of [...running]) if (animation.effect?.target?.contains(event.target)) {
      animation.cancel(); running.delete(animation);
    }
  });
  document.addEventListener('shopify:section:unload', event => cancel(event.target));
  document.addEventListener('shopify:section:load', event => { prepare(event.target); prepareImages(event.target); });
  document.addEventListener('shopify:section:select', event => cancel(event.target));
})();
